import React, { useState, useEffect, useCallback } from 'react';
import { manualTradingApi, fundApi } from '../../services/api';
import { TimePeriod, DateRange, ModelStatsMap, InvestorShare } from '../../types/analytics.types';
import { FundSummary } from '../../types/fund.types';
import { MODEL_KEYS } from '../../config/analytics';
import {
  getMLClosedTrades,
  filterTradesByDateRange,
  computeModelStats,
  computeAccountOverview,
  getOpenPositions,
  getModelSize,
} from '../../utils/tradeAnalytics';
import TimePeriodFilter from './components/TimePeriodFilter';
import AccountOverview from './components/AccountOverview';
import ModelPerformanceTable from './components/ModelPerformanceTable';
import ModelDetailKPIs from './components/ModelDetailKPIs';
import OpenPositions from './components/OpenPositions';
import InvestorsCapitalShare from './components/InvestorsCapitalShare';
import './Analytics.css';

const Analytics: React.FC = () => {
  const [allTrades, setAllTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fundSummary, setFundSummary] = useState<FundSummary | null>(null);

  // Filter state
  const [activePeriod, setActivePeriod] = useState<TimePeriod>('all_time');
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });

  // Selected model for detail KPIs
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  // Computed data
  const [filteredStatsMap, setFilteredStatsMap] = useState<ModelStatsMap>({});
  const [allTimeStatsMap, setAllTimeStatsMap] = useState<ModelStatsMap>({});

  const fetchData = useCallback(async () => {
    try {
      const [trades, summary] = await Promise.all([
        manualTradingApi.getTrades(),
        fundApi.getSummary(),
      ]);
      setAllTrades(trades || []);
      setFundSummary(summary);
    } catch (error) {
      console.error('[Analytics] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and poll every 60s
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Recompute stats when trades or date filter changes
  useEffect(() => {
    if (allTrades.length === 0) return;

    const allClosed = getMLClosedTrades(allTrades);

    // All-time stats
    const allTimeStats = computeModelStats(allClosed);
    setAllTimeStatsMap(allTimeStats);

    // Filtered stats
    const filtered = filterTradesByDateRange(allClosed, dateRange.from || undefined, dateRange.to || undefined);
    const filteredStats = computeModelStats(filtered);
    setFilteredStatsMap(filteredStats);

    // Auto-select first model with trades if none selected
    if (!selectedModel) {
      const firstActive = MODEL_KEYS.find(k => filteredStats[k] && filteredStats[k].totalTrades > 0);
      if (firstActive) setSelectedModel(firstActive);
    }
  }, [allTrades, dateRange, selectedModel]);

  // Model sizes (from most recent trade per model)
  const modelSizes = React.useMemo(() => {
    const allClosed = getMLClosedTrades(allTrades);
    const sizes: Record<string, number> = {};
    MODEL_KEYS.forEach(k => {
      sizes[k] = getModelSize(allClosed, k);
    });
    return sizes;
  }, [allTrades]);

  const totalInvested = fundSummary?.totalInvestedCapital ?? 0;
  const navPerUnit = fundSummary?.navPerUnit ?? 1;

  const accountOverview = React.useMemo(() => {
    const overview = computeAccountOverview(filteredStatsMap, totalInvested);
    return { ...overview, navPerUnit };
  }, [filteredStatsMap, totalInvested, navPerUnit]);

  const allTimeOverview = React.useMemo(
    () => computeAccountOverview(allTimeStatsMap, totalInvested),
    [allTimeStatsMap, totalInvested]
  );

  // Derive investor shares from fund summary API
  const investorShares: InvestorShare[] = React.useMemo(() => {
    if (!fundSummary) return [];
    return fundSummary.investors.map(inv => ({
      name: inv.name,
      ownershipPct: inv.ownershipPct,
      netInvested: inv.netInvested,
      unitsOwned: inv.unitsOwned,
      currentValue: inv.currentValue,
      profitLoss: inv.profitLoss,
      returnPct: inv.returnPct,
    }));
  }, [fundSummary]);

  const openPositions = React.useMemo(() => getOpenPositions(allTrades), [allTrades]);

  const handlePeriodChange = (period: TimePeriod, range?: DateRange) => {
    setActivePeriod(period);
    if (range) setDateRange(range);
  };

  const handleCustomDateChange = (range: DateRange) => {
    setActivePeriod('custom');
    setDateRange(range);
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="page-header">
          <h2>Analytics</h2>
          <p className="page-description">Account performance and model analytics</p>
        </div>
        <div className="analytics-loading">Loading trade data...</div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h2>Analytics</h2>
        <p className="page-description">Account performance and model analytics</p>
      </div>

      <div className="analytics-card">
        <TimePeriodFilter
          activePeriod={activePeriod}
          dateRange={dateRange}
          onPeriodChange={handlePeriodChange}
          onCustomDateChange={handleCustomDateChange}
        />

        <AccountOverview data={accountOverview} allTimeBalance={allTimeOverview.currentBalance} />

        <ModelPerformanceTable
          statsMap={filteredStatsMap}
          selectedModel={selectedModel}
          onSelectModel={setSelectedModel}
          modelSizes={modelSizes}
        />

        {selectedModel && (
          <ModelDetailKPIs
            selectedModel={selectedModel}
            statsMap={filteredStatsMap}
            onSelectModel={setSelectedModel}
          />
        )}

        <OpenPositions positions={openPositions} />

        <InvestorsCapitalShare shares={investorShares} />
      </div>
    </div>
  );
};

export default Analytics;
